import React, { useEffect, useRef, useState } from "react";
import { assetUrl } from "../api/http";

type PendantPhotoCustomizationSelection = {
  imageUrl?: string;
  scale?: number;
  rotation?: number;
  offsetXPct?: number;
  offsetYPct?: number;
  text?: string;
  textFont?: string;
  textSizePx?: number;
  textOffsetXPct?: number;
  textOffsetYPct?: number;
  textColor?: string;
  textWeight?: number;
  textItalic?: boolean;
  textStroke?: boolean;
  textUppercase?: boolean;
  textLetterSpacingPx?: number;
  localFile?: File;
  area?: any;
};

type CupPreviewViewMode = "default" | "front" | "text";

type PreviewProps = {
  value: PendantPhotoCustomizationSelection | null;
  onChange?: (value: PendantPhotoCustomizationSelection | null) => void;
  className?: string;
  viewMode?: CupPreviewViewMode;
};

const CUP_HEIGHT_IN = 3.7;
const CUP_DIAMETER_IN = 3.2;
const HANDLE_WIDTH_IN = 1;
const CUP_CIRCUMFERENCE_IN = Math.PI * CUP_DIAMETER_IN;
const HANDLE_CLEAR_FRACTION = Math.min(Math.max(HANDLE_WIDTH_IN / CUP_CIRCUMFERENCE_IN, 0, 0.49), 0, 0.49);
const PRINTABLE_FRACTION = 1 - HANDLE_CLEAR_FRACTION;
const HANDLE_ANGLE_RAD = -Math.PI / 2;
const HANDLE_CENTER_U = ((HANDLE_ANGLE_RAD / (2 * Math.PI)) % 1 + 1) % 1;
const HANDLE_GAP_START_U = ((HANDLE_CENTER_U - HANDLE_CLEAR_FRACTION / 2) % 1 + 1) % 1;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const normalizeDeg = (deg: number) => ((deg % 360) + 360) % 360;
const wrap01 = (n: number) => { const v = n % 1; return v < 0 ? v + 1 : v; };
const circularInRange01 = (u: number, start: number, width: number) => {
  const shifted = wrap01(u - start);
  return shifted < width;
};

function ensureCanvasPixelSize(canvas: HTMLCanvasElement, w: number, h: number) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const pxWidth = Math.max(1, Math.round(w * dpr));
  const pxHeight = Math.max(1, Math.round(h * dpr));
  if (canvas.width !== pxWidth || canvas.height !== pxHeight) {
    canvas.width = pxWidth;
    canvas.height = pxHeight;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function drawMug(
  ctx: CanvasRenderingContext2D,
  bounds: { w: number; h: number },
  texCanvas: HTMLCanvasElement,
  inputs: {
    loadedImage: HTMLImageElement | null;
    scale: number;
    rotation: number;
    wrapOffsetPct: number;
    verticalOffsetPct: number;
    text: string;
    textFont: string;
    textSizePx: number;
    textOffsetXPct: number;
    textOffsetYPct: number;
    textColor: string;
    textWeight: number;
    textItalic: boolean;
    textStroke: boolean;
    textUppercase: boolean;
    textLetterSpacingPx: number;
    spinDeg: number;
    showEmptyHint?: boolean;
  }
) {
  const { w, h } = bounds;
  const {
    loadedImage, scale, rotation, wrapOffsetPct, verticalOffsetPct,
    text, textFont, textSizePx, textOffsetXPct, textOffsetYPct,
    textColor, textWeight, textItalic, textStroke, textUppercase,
    textLetterSpacingPx, spinDeg,
  } = inputs;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#e9edf2";
  ctx.fillRect(0, 0, w, h);

  const mugAspect = CUP_HEIGHT_IN / CUP_DIAMETER_IN;
  const bodyW = Math.min(w * 0.68, (h * 0.74) / mugAspect);
  const bodyH = bodyW * mugAspect;
  const bodyX = (w - bodyW) / 2;
  const bodyY = (h - bodyH) / 2 + h * 0.02;
  const bodyR = bodyW / 2;
  const centerX = bodyX + bodyR;

  const topCurve = bodyW * 0.045;
  const bottomCurve = bodyW * 0.05;

  const topEdgeAt = (x: number) => {
    const nx = clamp((x - centerX) / bodyR, -1, 1);
    const curve = Math.sqrt(Math.max(0, 1 - nx * nx));
    return bodyY + (1 - curve) * topCurve;
  };
  const bottomEdgeAt = (x: number) => {
    const nx = clamp((x - centerX) / bodyR, -1, 1);
    const curve = Math.sqrt(Math.max(0, 1 - nx * nx));
    return bodyY + bodyH - (1 - curve) * bottomCurve;
  };

  const drawCupPath = () => {
    const samples = 160;
    ctx.beginPath();
    for (let i = 0; i <= samples; i += 1) {
      const x = bodyX + (i / samples) * bodyW;
      const y = topEdgeAt(x);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    for (let i = samples; i >= 0; i -= 1) {
      const x = bodyX + (i / samples) * bodyW;
      const y = bottomEdgeAt(x);
      ctx.lineTo(x, y);
    }
    ctx.closePath();
  };

  ctx.save();
  ctx.fillStyle = "rgba(15, 23, 42, 0.08)";
  ctx.beginPath();
  ctx.ellipse(centerX, bodyY + bodyH + 18, bodyW * 0.46, bodyH * 0.07, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const spinRad = (normalizeDeg(spinDeg) * Math.PI) / 180;
  const handleAngle = HANDLE_ANGLE_RAD + spinRad;
  const sinA = Math.sin(handleAngle);
  const cosA = Math.cos(handleAngle);
  const side = sinA >= 0 ? 1 : -1;
  const absSin = Math.abs(sinA);
  const handleBehind = cosA < -0.06;
  const handleVisible = handleBehind ? 0 : clamp(0.25 + absSin * 0.75, 0.25, 1);
  const handleAttachX = centerX + bodyR * sinA * 0.96;
  const handleTopY = bodyY + bodyH * 0.28;
  const handleBottomY = bodyY + bodyH * 0.72;
  const handleDepth = bodyW * 0.2 * (0.5 + 0.5 * handleVisible);

  const drawHandle = () => {
    if (handleVisible <= 0.01) return;
    ctx.save();
    const outer = ctx.createLinearGradient(handleAttachX, 0, handleAttachX + side * handleDepth, 0);
    outer.addColorStop(0, "#ffffff");
    outer.addColorStop(1, "#e5e7eb");
    ctx.strokeStyle = outer;
    ctx.lineWidth = bodyW * 0.11 * handleVisible;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(handleAttachX, handleTopY);
    ctx.bezierCurveTo(
      handleAttachX + side * handleDepth, bodyY + bodyH * 0.2,
      handleAttachX + side * handleDepth, bodyY + bodyH * 0.82,
      handleAttachX, handleBottomY
    );
    ctx.stroke();

    ctx.strokeStyle = "rgba(148, 163, 184, 0.42)";
    ctx.lineWidth = bodyW * 0.045 * handleVisible;
    ctx.beginPath();
    ctx.moveTo(handleAttachX + side * bodyW * 0.005, handleTopY + bodyW * 0.02);
    ctx.bezierCurveTo(
      handleAttachX + side * handleDepth * 0.68, bodyY + bodyH * 0.24,
      handleAttachX + side * handleDepth * 0.68, bodyY + bodyH * 0.76,
      handleAttachX + side * bodyW * 0.005, handleBottomY - bodyW * 0.02
    );
    ctx.stroke();
    ctx.restore();
  };

  const texW = 1400;
  const texH = 980;
  if (texCanvas.width !== texW || texCanvas.height !== texH) {
    texCanvas.width = texW;
    texCanvas.height = texH;
  }
  const tctx = texCanvas.getContext("2d");
  if (!tctx) return;
  tctx.clearRect(0, 0, texW, texH);
  tctx.fillStyle = "#ffffff";
  tctx.fillRect(0, 0, texW, texH);

  if (loadedImage) {
    const baseScale = Math.max(texW / loadedImage.width, texH / loadedImage.height);
    const drawW = loadedImage.width * baseScale;
    const drawH = loadedImage.height * baseScale;
    const shiftY = (verticalOffsetPct / 100) * texH * 0.6;
    tctx.save();
    tctx.translate(texW / 2, texH / 2 + shiftY);
    tctx.rotate((rotation * Math.PI) / 180);
    tctx.scale(scale, scale);
    tctx.drawImage(loadedImage, -drawW / 2, -drawH / 2, drawW, drawH);
    tctx.restore();
  }

  const styledText = textUppercase ? String(text || "").toUpperCase() : String(text || "");
  const hasArtworkTexture = !!loadedImage || !!styledText.trim();
  if (styledText.trim()) {
    const sizePx = clamp(Number(textSizePx || 72), 14, 320);
    const weight = clamp(Number(textWeight || 700), 100, 900);
    const italic = textItalic ? "italic " : "";
    const family = textFont || "Impact";
    const letterSpacing = Number(textLetterSpacingPx || 0);
    const centerX = texW / 2 + (Number(textOffsetXPct || 0) / 100) * texW * 0.48;
    const centerY = texH / 2 + (Number(textOffsetYPct || 0) / 100) * texH * 0.48;

    tctx.save();
    tctx.fillStyle = textColor || "#111827";
    tctx.font = `${italic}${weight} ${Math.round(sizePx)}px "${family}"`;
    tctx.textAlign = "center";
    tctx.textBaseline = "middle";

    if (textStroke) {
      tctx.strokeStyle = "#ffffff";
      tctx.lineWidth = Math.max(1.2, sizePx * 0.09);
      tctx.strokeText(styledText, centerX, centerY);
    }

    if (Math.abs(letterSpacing) < 0.1) {
      tctx.fillText(styledText, centerX, centerY);
    } else {
      const measured = tctx.measureText(styledText);
      let x = centerX - measured.width / 2 - ((styledText.length - 1) * letterSpacing) / 2;
      for (const ch of styledText) {
        const m = tctx.measureText(ch);
        const charW = m.width;
        tctx.fillText(ch, x + charW / 2, centerY);
        x += charW + letterSpacing;
      }
    }
    tctx.restore();
  }

  const spinTurns = normalizeDeg(spinDeg) / 360;
  const wrapTurns = wrapOffsetPct / 100;
  const gapStartU = HANDLE_GAP_START_U;
  const stripes = clamp(Math.round(bodyW * 0.62), 220, 320);

  ctx.save();
  drawCupPath();
  ctx.clip();

  for (let i = 0; i < stripes; i += 1) {
    const t0 = -Math.PI / 2 + (i / stripes) * Math.PI;
    const t1 = -Math.PI / 2 + ((i + 1) / stripes) * Math.PI;
    const tm = (t0 + t1) / 2;

    const x0 = centerX + bodyR * Math.sin(t0);
    const x1 = centerX + bodyR * Math.sin(t1);
    const xLeft = Math.min(x0, x1);
    const stripeW = Math.max(1.4, Math.abs(x1 - x0) + 1.4);
    const xMid = xLeft + stripeW / 2;
    const top = topEdgeAt(xMid);
    const bottom = bottomEdgeAt(xMid);
    const stripeH = Math.max(1, bottom - top);

    const cupU = wrap01(tm / (2 * Math.PI) - spinTurns);
    const inHandleGap = circularInRange01(cupU, gapStartU, HANDLE_CLEAR_FRACTION);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(xLeft, top, stripeW, stripeH);

    if (!inHandleGap && hasArtworkTexture) {
      const shiftedCup = wrap01(cupU - gapStartU);
      const printableU = (shiftedCup - HANDLE_CLEAR_FRACTION) / PRINTABLE_FRACTION;
      const designU = wrap01(printableU + wrapTurns);
      const sx = Math.floor(clamp(designU, 0, 0.999999) * (texW - 2));
      ctx.drawImage(texCanvas, sx, 0, 2, texH, xLeft - 0.5, top, stripeW + 1, stripeH);
    }
  }

  const smoothShade = ctx.createLinearGradient(bodyX, 0, bodyX + bodyW, 0);
  smoothShade.addColorStop(0, "rgba(15, 23, 42, 0.20)");
  smoothShade.addColorStop(0.16, "rgba(15, 23, 42, 0.09)");
  smoothShade.addColorStop(0.5, "rgba(255, 255, 255, 0.03)");
  smoothShade.addColorStop(0.84, "rgba(15, 23, 42, 0.09)");
  smoothShade.addColorStop(1, "rgba(15, 23, 42, 0.20)");
  ctx.fillStyle = smoothShade;
  ctx.fillRect(bodyX, bodyY - 1, bodyW, bodyH + 2);

  ctx.restore();

  ctx.save();
  drawCupPath();
  ctx.strokeStyle = "rgba(148, 163, 184, 0.75)";
  ctx.lineWidth = Math.max(1, bodyW * 0.01);
  ctx.stroke();
  ctx.restore();

  drawHandle();

  if (inputs.showEmptyHint && !loadedImage && !styledText.trim()) {
    ctx.save();
    ctx.fillStyle = "rgba(15, 23, 42, 0.55)";
    ctx.font = "600 12px ui-sans-serif, system-ui, -apple-system, Segoe UI";
    ctx.textAlign = "center";
    ctx.fillText("Upload an image to preview your cup design", centerX, bodyY + bodyH * 0.56);
    ctx.restore();
  }
}

export function Cup3DPreview({ value, onChange, className, viewMode = "default" }: PreviewProps) {
  const [spinDeg, setSpinDeg] = useState<number>(0);
  const [dragging, setDragging] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const texCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [resizeTick, setResizeTick] = useState(0);
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gestureRef = useRef<{
    mode: "none" | "spin" | "pinch-image" | "drag-image" | "drag-text";
    startX: number;
    startY: number;
    startSpin: number;
    initialDistance: number;
    initialScale: number;
    initialWrapOffset: number;
    initialVerticalOffset: number;
    initialTextX: number;
    initialTextY: number;
  }>({
    mode: "none",
    startX: 0,
    startY: 0,
    startSpin: 0,
    initialDistance: 0,
    initialScale: 1,
    initialWrapOffset: 0,
    initialVerticalOffset: 0,
    initialTextX: 0,
    initialTextY: 0,
  });

  const imageUrl = value?.imageUrl || "";
  const scale = value?.scale ?? 1;
  const rotation = value?.rotation ?? 0;
  const wrapOffsetPct = value?.offsetXPct ?? 0;
  const verticalOffsetPct = value?.offsetYPct ?? 0;
  const text = value?.text || "";
  const textFont = value?.textFont || "Impact";
  const textSizePx = value?.textSizePx ?? 72;
  const textOffsetXPct = value?.textOffsetXPct ?? 0;
  const textOffsetYPct = value?.textOffsetYPct ?? 0;
  const textColor = value?.textColor || "#111827";
  const textWeight = value?.textWeight ?? 900;
  const textItalic = value?.textItalic ?? false;
  const textStroke = value?.textStroke ?? false;
  const textUppercase = value?.textUppercase ?? true;
  const textLetterSpacingPx = value?.textLetterSpacingPx ?? 1;

  useEffect(() => {
    if (!imageUrl) {
      setLoadedImage(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!cancelled) setLoadedImage(img);
    };
    img.onerror = () => {
      if (!cancelled) setLoadedImage(null);
    };
    img.src = assetUrl(imageUrl) || imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => setResizeTick((t) => t + 1));
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const ctx = ensureCanvasPixelSize(canvas, bounds.width, bounds.height);
    if (!ctx) return;

    if (!texCanvasRef.current) texCanvasRef.current = document.createElement("canvas");
    drawMug(ctx, { w: bounds.width, h: bounds.height }, texCanvasRef.current, {
      loadedImage, scale, rotation, wrapOffsetPct, verticalOffsetPct,
      text, textFont, textSizePx, textOffsetXPct, textOffsetYPct,
      textColor, textWeight, textItalic, textStroke, textUppercase,
      textLetterSpacingPx, spinDeg,
      showEmptyHint: !imageUrl && !text.trim(),
    });
  }, [
    imageUrl, loadedImage, rotation, scale, spinDeg, text, textColor, textFont,
    textItalic, textLetterSpacingPx, textOffsetXPct, textOffsetYPct, textSizePx,
    textStroke, textUppercase, textWeight, verticalOffsetPct, wrapOffsetPct, resizeTick,
  ]);

  const onPointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (viewMode !== "default") return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const points = Array.from(pointersRef.current.values());
    
    if (points.length === 1 && onChange) {
      if (event.shiftKey && text.trim()) {
        gestureRef.current.mode = "drag-text";
        gestureRef.current.startX = event.clientX;
        gestureRef.current.startY = event.clientY;
        gestureRef.current.initialTextX = textOffsetXPct;
        gestureRef.current.initialTextY = textOffsetYPct;
        setDragging(true);
        return;
      }

      if (event.altKey && imageUrl) {
        gestureRef.current.mode = "drag-image";
        gestureRef.current.startX = event.clientX;
        gestureRef.current.startY = event.clientY;
        gestureRef.current.initialWrapOffset = wrapOffsetPct;
        gestureRef.current.initialVerticalOffset = verticalOffsetPct;
        setDragging(true);
        return;
      }
    }

    if (points.length >= 2 && !!imageUrl && onChange) {
      const [a, b] = points;
      gestureRef.current.mode = "pinch-image";
      gestureRef.current.initialDistance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      gestureRef.current.initialScale = scale;
      setDragging(true);
      return;
    }

    gestureRef.current.mode = "spin";
    gestureRef.current.startX = event.clientX;
    gestureRef.current.startSpin = spinDeg;
    setDragging(true);
  };
  
  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (viewMode !== "default") return;
    if (!pointersRef.current.has(event.pointerId)) return;
    event.preventDefault();
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(pointersRef.current.values());

    if (gestureRef.current.mode === "pinch-image" && points.length >= 2 && !!imageUrl && value && onChange) {
      const [a, b] = points;
      const distance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      const scaleRatio = distance / Math.max(1, gestureRef.current.initialDistance);
      const newScale = clamp(gestureRef.current.initialScale * scaleRatio, 0.4, 3);
      onChange({ ...value, scale: newScale });
      return;
    }

    if (gestureRef.current.mode === "drag-text" && value && onChange) {
      const dx = event.clientX - gestureRef.current.startX;
      const dy = event.clientY - gestureRef.current.startY;
      onChange({
        ...value,
        textOffsetXPct: clamp(gestureRef.current.initialTextX + dx * 0.3, -100, 100),
        textOffsetYPct: clamp(gestureRef.current.initialTextY + dy * 0.3, -100, 100),
      });
      return;
    }

    if (gestureRef.current.mode === "drag-image" && value && onChange) {
      const dx = event.clientX - gestureRef.current.startX;
      const dy = event.clientY - gestureRef.current.startY;
      onChange({
        ...value,
        offsetXPct: clamp(gestureRef.current.initialWrapOffset + dx * 0.3, -200, 200),
        offsetYPct: clamp(gestureRef.current.initialVerticalOffset + dy * 0.3, -80, 80),
      });
      return;
    }

    if (gestureRef.current.mode !== "spin") return;
    const dx = event.clientX - gestureRef.current.startX;
    setSpinDeg(normalizeDeg(gestureRef.current.startSpin + dx * 0.45));
  };
  
  const onPointerEnd: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (viewMode !== "default") return;
    const el = event.currentTarget;
    if (el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    pointersRef.current.delete(event.pointerId);
    const points = Array.from(pointersRef.current.values());

    if (points.length >= 2 && !!imageUrl && onChange) {
      const [a, b] = points;
      gestureRef.current.mode = "pinch-image";
      gestureRef.current.initialDistance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      gestureRef.current.initialScale = scale;
      setDragging(true);
      return;
    }

    if (points.length === 1 && onChange) {
      const [p] = points;
      const lastEvent = event.nativeEvent;
      if (lastEvent.shiftKey && text.trim()) {
        gestureRef.current.mode = "drag-text";
        gestureRef.current.startX = p.x;
        gestureRef.current.startY = p.y;
        gestureRef.current.initialTextX = textOffsetXPct;
        gestureRef.current.initialTextY = textOffsetYPct;
      } else if (lastEvent.altKey && imageUrl) {
        gestureRef.current.mode = "drag-image";
        gestureRef.current.startX = p.x;
        gestureRef.current.startY = p.y;
        gestureRef.current.initialWrapOffset = wrapOffsetPct;
        gestureRef.current.initialVerticalOffset = verticalOffsetPct;
      } else {
        gestureRef.current.mode = "spin";
        gestureRef.current.startX = p.x;
        gestureRef.current.startSpin = spinDeg;
      }
      setDragging(true);
      return;
    }

    gestureRef.current.mode = "none";
    setDragging(false);
  };

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      style={{
        width: "100%",
        height: "100%",
        cursor: viewMode === "default" ? (dragging ? "grabbing" : "grab") : "default",
        touchAction: viewMode === "default" ? "none" : undefined,
      }}
    >
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}
